// src/utils/assetUtils.ts
import { Asset } from '../types/assets';

/**
 * Constructs a full URL for accessing an asset's content
 * Now supports clear company directory structure: /uploads/companyId-123/filename.ext
 * @param asset - The asset object containing the contentUrl
 * @returns Full URL for accessing the asset
 */
export const getAssetUrl = (asset: Asset): string => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://localhost:7077';
  
  // Remove trailing slash from base URL if present
  const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  
  // The contentUrl should now include the company path: /uploads/companyId-123/filename.ext
  // Ensure contentUrl starts with /
  const contentPath = asset.contentUrl.startsWith('/') ? asset.contentUrl : `/${asset.contentUrl}`;
  
  const fullUrl = `${baseUrl}${contentPath}`;
  console.log('Asset URL constructed:', fullUrl);
  console.log('ContentUrl from database:', asset.contentUrl);
  
  return fullUrl;
};

/**
 * Constructs a full URL for accessing an asset's content by contentUrl string
 * @param contentUrl - The relative content URL path (includes company path)
 * @returns Full URL for accessing the asset
 */
export const getAssetUrlFromPath = (contentUrl: string): string => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://localhost:7077';
  
  // Remove trailing slash from base URL if present
  const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  
  // Ensure contentUrl starts with /
  const contentPath = contentUrl.startsWith('/') ? contentUrl : `/${contentUrl}`;
  
  return `${baseUrl}${contentPath}`;
};

/**
 * Extracts company ID from asset URL for debugging/validation
 * @param asset - The asset object
 * @returns Company ID if found in path, null otherwise
 */
export const getCompanyIdFromAssetUrl = (asset: Asset): number | null => {
  // Expected format: /uploads/companyId-123/filename.ext
  const pathMatch = asset.contentUrl.match(/\/uploads\/companyId-(\d+)\//);
  return pathMatch ? parseInt(pathMatch[1], 10) : null;
};

/**
 * Validates that asset URL contains company-specific path
 * @param asset - The asset to validate
 * @returns true if URL follows company-based structure
 */
export const isValidCompanyAssetUrl = (asset: Asset): boolean => {
  // Check if URL follows pattern: /uploads/companyId-{number}/filename
  return /^\/uploads\/companyId-\d+\//.test(asset.contentUrl);
};

/**
 * Gets a human-readable description of the asset's company directory
 * @param asset - The asset object
 * @returns Description like "Company 123" or "Unknown Company"
 */
export const getAssetCompanyDescription = (asset: Asset): string => {
  const companyId = getCompanyIdFromAssetUrl(asset);
  return companyId ? `Company ${companyId}` : 'Unknown Company';
};

/**
 * Formats file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Formats duration in MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string or 'N/A' if no duration
 */
export const formatDuration = (seconds?: number): string => {
  if (!seconds) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Checks if an asset is an image type
 * @param asset - The asset to check
 * @returns true if the asset is an image
 */
export const isImageAsset = (asset: Asset): boolean => {
  return asset.fileType.toLowerCase() === 'image';
};

/**
 * Checks if an asset is a video type
 * @param asset - The asset to check
 * @returns true if the asset is a video
 */
export const isVideoAsset = (asset: Asset): boolean => {
  return asset.fileType.toLowerCase() === 'video';
};

/**
 * Gets the appropriate MIME type for an asset based on its file format
 * @param asset - The asset to get MIME type for
 * @returns MIME type string
 */
export const getAssetMimeType = (asset: Asset): string => {
  const format = asset.fileFormat.toLowerCase();
  
  if (isImageAsset(asset)) {
    switch (format) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'bmp':
        return 'image/bmp';
      default:
        return 'image/jpeg';
    }
  }
  
  if (isVideoAsset(asset)) {
    switch (format) {
      case 'mp4':
        return 'video/mp4';
      case 'avi':
        return 'video/x-msvideo';
      case 'mov':
        return 'video/quicktime';
      case 'wmv':
        return 'video/x-ms-wmv';
      case 'webm':
        return 'video/webm';
      default:
        return 'video/mp4';
    }
  }
  
  return 'application/octet-stream';
};